import { useContext, useMemo } from "react"
import { useAppSelector } from "@/reducers/hooks"
import { type Message as MessageType } from "@/reducers/messagesSlice"
import {
  findAuthorProfile,
  messageAuthorName,
} from "@/reducers/profilesSliceSelectors"
import {
  selectActiveChat,
  selectDriverParticipant,
} from "@/reducers/chatsSliceSelectors"
import { participantPhotoToSrc } from "@/components/AvatarImage"
import { XmppContext } from "@/stream/xmppClient"
import ChatHelpers from "@/lib/chatHelpers"
import { jid } from "@xmpp/client"
import { selectAccountChatByJid } from "@/reducers/accountChatsSlice"
import { getTransicsMessagePresentation } from "@/lib/transicsMessagePresentation"

export const useForwardedMessageData = (message: MessageType) => {
  const { myJid } = useContext(XmppContext)
  const activeChat = useAppSelector(selectActiveChat)
  const sourceChatJid = useMemo(() => {
    try {
      return jid(message.from).bare().toString()
    } catch {
      return ""
    }
  }, [message.from])
  const sourceAccountChatSelector = useMemo(
    () => selectAccountChatByJid(sourceChatJid),
    [sourceChatJid],
  )
  const sourceAccountChat = useAppSelector(sourceAccountChatSelector)
  const transicsPresentation = getTransicsMessagePresentation(
    message,
    sourceAccountChat,
  )
  const authorProfile = useAppSelector(
    findAuthorProfile(message, myJid.toString()),
  )
  const authorName = useAppSelector(messageAuthorName(message, myJid))
  const authorInitials = authorName
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  const driverParticipant = useAppSelector(
    selectDriverParticipant(activeChat.jid),
  )

  const avatarSrc = participantPhotoToSrc(authorProfile?.photo)

  const avatarColor = useMemo(() => {
    const chatColor = ChatHelpers.getChatColor(activeChat)
    if (chatColor) return chatColor

    // // Fallback colors based on chat state
    // if (isArchived) return "#cccccc"
    // if (accountChats[jid]?.group === "shared_driver") return "#ffecb8"

    return "var(--defaultAvatarColor)"
  }, [activeChat])

  return {
    activeChat,
    authorProfile,
    authorName,
    displayBody: transicsPresentation?.body,
    displayBodyOffset: transicsPresentation?.bodyOffset,
    authorInitials,
    avatarColor,
    driverParticipant,
    avatarSrc,
  }
}
